<template>
  <div class="container-fluid page-container">
    <div v-if="lead" class="row page-header__container  mb-4 pb-3">
      <div class="col">
        <page-heading
          :class="'align-items-center flex-row justify-content-start'"
          :back-url="'my-leads'"
          :url-params="{pagination: backPage}"
          :title="pageTitle"
        />
      </div>
    </div>

    <div class="row">
      <div class="col">
        <b-alert dismissible :show="dismissableError" variant="warning" @dismissed="dismissableError=null">
          {{ dismissableError }}
        </b-alert>
      </div>
    </div>

    <div class="row">
      <div class="col">
        <b-alert :show="leadMessage !== null" variant="info" v-html="leadMessage" />
      </div>
    </div>

    <div v-if="lead" class="row mt-3">
      <div class="col-12 col-md-8">
        <b-card :class="'border card overflow-hidden rounded shadow mb-3'" no-body>
          <b-card-header :header-class="'card-header__accent'">
            {{ $t('Lead information') }}
          </b-card-header>
          <b-card-body :body-class="'p-4'">
            <div class="row">
              <div class="col-12 col-sm-5">
                <div class="row">
                  <div class="col-12 mt-2">
                    <div class="row lead__details line-height-6">
                      <div class="col-md-12">
                        <div class="dt__icon">
                          <font-awesome-icon icon="user" />
                        </div>
                        <div class="dd__text">
                          {{ $t('fullName', {title: lead.enquiry.title, forename: lead.enquiry.forename, surname: lead.enquiry.surname }) }}
                        </div>
                        <div class="dt__icon">
                          <font-awesome-icon icon="phone" />
                        </div>
                        <div class="dd__text">
                          <a :href="'tel:' + lead.enquiry.telephone">{{ lead.enquiry.telephone }}</a>
                        </div>
                        <div class="dt__icon" />
                        <div class="dd__text">
                          <a :href="'tel:' + lead.enquiry.alternate_phone">{{ lead.enquiry.alternate_phone }}</a>
                        </div>
                        <div class="dt__icon">
                          <font-awesome-icon icon="envelope" />
                        </div>
                        <div class="dd__text">
                          <a :href="'mailto:' + lead.enquiry.email">{{ lead.enquiry.email }}</a>
                        </div>
                        <div class="dt__icon">
                          <font-awesome-icon icon="map-marker-alt" />
                        </div>
                        <div
                          class="dd__text"
                          @click="openInNewTab(lead.enquiry)"
                        >
                          <div v-if="lead.enquiry.address1" class="col-12 px-0">
                            {{ lead.enquiry.address1 }}
                          </div>
                          <div v-if="lead.enquiry.address2" class="col-12 px-0">
                            {{ lead.enquiry.address2 }}
                          </div>
                          <div v-if="lead.enquiry.town" class="col-12 px-0">
                            {{ lead.enquiry.town }}<br>
                          </div>
                          <div class="col-12 px-0">
                            {{ lead.enquiry.postcode }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="isMobile" class="col-12 mt-2 px-0 d-flex justify-content-center align-items-center enquiry-telephone">
                    <a class="btn btn-sm btn-primary" :href="'tel:'+lead.enquiry.telephone">
                      <font-awesome-icon :icon="'phone'" rotation="90" class="mr-2" />{{ $t('CALL') }}
                    </a>
                  </div>

                  <div class="col-12 my-2">
                    <div class="lead__details customer-notes font-weight-normal p-3 mt-3 mb-4 mb-0 mt-md-4 line-height-5">
                      <h2 class="h4">
                        {{ $t('Qualification notes:') }}
                      </h2>
                      {{ lead.enquiry.description }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-12 col-sm-7">
                <b-table
                  class="bg-white d-table table-borderless table-striped table-responsive"
                  striped
                  :items="questions"
                  thead-class="d-none"
                />
              </div>
            </div>
            <div class="row lead-location ">
              <div class="col-12 pt-3">
                <lead-map :marker="mapMarker" />
              </div>
            </div>
          </b-card-body>
        </b-card>
      </div>

      <div class="col-12 col-md-4 lead__sidebar">
        <b-card :class="'order-0 border card overflow-hidden rounded shadow'" no-body>
          <b-card-header>
            {{ $t('Administration') }}
          </b-card-header>
          <b-card-body>
            <div class="p-2 p-xxl-4">
              <div class="administration__actions align-items-center justify-content-lg-between d-flex flex-row flex-wrap">
                <div v-if="appealExpired" class=" alert alert-info small order-0 col-12">
                  {{ $t('Your ability to appeal the rejection of this lead report closed on: {expiryDate}', { expiryDate: $d(reportBy, 'shortWithTime') }) }}
                </div>
                <div v-else-if="reportExpired" class=" alert alert-info small order-0 col-12">
                  {{ $t('Your ability to report this lead closed on: {expiryDate}', { expiryDate: reportByFormatted }) }}
                </div>

                <div v-if="isReportable" class="alert alert-info small order-0 col-12">
                  {{ $t('You can report this lead until: {expiryDate}', { expiryDate: reportByFormatted }) }}
                </div>

                <div v-if="lead.isAppealable && !appealExpired" class="alert alert-info small order-0 col-12">
                  {{ $t('You can appeal our decision to reject your lead report until: {expiryDate}', { expiryDate: $d(appealByParsed, 'shortWithTime') }) }}
                </div>

                <a v-if="isDesktop" class="btn btn-outline-secondary my-1" :href="printUrl(lead.code)" target="_blank">
                  <font-awesome-icon class="mr-2" :icon="'print'" /> {{ $t('Print') }}
                </a>
                <a class="btn btn-outline-secondary my-1" :href="generateLeadDetailsPdf(lead)" target="_blank">
                  <font-awesome-icon class="mr-2" :icon="'download'" /> {{ $t('PDF') }}
                </a>
                <router-link
                  v-if="showInvoicePdf"
                  :class="['btn', 'btn-outline-secondary', 'my-1', {disabled: invoiceCode === null}]"
                  :to="{name: 'invoices', query: {code: invoiceCode}}"
                  :aria-disabled="invoiceCode === null"
                  target="_blank"
                >
                  {{ $t('View Invoice') }}
                </router-link>
                <router-link
                  v-if="creditCode !== null"
                  :class="['btn', 'btn-outline-secondary', 'my-1']"
                  :to="{name: 'invoices', query: {code: creditCode}}"
                  target="_blank"
                >
                  {{ $t('View Credit') }}
                </router-link>

                <b-button class="my-1" variant="outline-secondary" @click="askConfirmation">
                  {{ (lead.dashboard_lead && lead.dashboard_lead.archived) ? $t('Unarchive') : $t('Archive') }}
                </b-button>
                <b-button
                  variant="outline-secondary"
                  :class="['btn', ' btn-outline-secondary', 'my-1', {'disabled': !remindFeedback || feedbackTooLate}]"
                  :aria-disabled="!remindFeedback || feedbackTooLate"
                  :disabled="!remindFeedback || feedbackTooLate"
                  @click="askFeedbackConfirmation(lead)"
                >
                  <span
                    v-b-tooltip.hover
                    class="d-flex justify-content-center w-100 stretched-link"
                    :title="feedbackReminderTitle"
                  >{{ $t('Get a Review') }}</span>
                </b-button>
                <router-link
                  v-if="!lead.isAppealable"
                  :to="{name: 'lead-report', params: {code: lead.code, lead: lead, action:'report'}}"
                  :class="['btn', ' btn-outline-secondary', 'my-1', {'disabled': !isReportable}]"
                  :aria-disabled="!isReportable"
                >
                  <span
                    v-b-tooltip.hover
                    class="d-flex justify-content-center w-100 stretched-link"
                    :title="reportTooltip"
                  >{{ $t('Report') }}</span>
                </router-link>
                <router-link
                  v-if="lead.isAppealable && !reportBanned && !appealExpired"
                  :to="{name: 'lead-report', params: {id: lead.code, lead: lead, action:'appeal'}}"
                  class="btn btn-outline-secondary my-1"
                >
                  {{ $t('Appeal') }}
                </router-link>
              </div>
            </div>
          </b-card-body>
        </b-card>
        <finance-module v-if="lead.company.can_offer_finance" :lead="lead" />
        <lead-review v-if="lead.feedback" class="full-width order-2 order-md-1" :lead="lead" />
        <b-card class="order-1 order-md-2 border card overflow-hidden rounded shadow mt-4 mt-sm-0 mt-md-4" no-body>
          <b-card-header>
            {{ $t('Notes') }}
          </b-card-header>
          <b-card-body>
            <lead-notes v-model="note" :notes="lead.notes" :lead-code="lead.code" :busy="noteBusy" />
          </b-card-body>
        </b-card>
      </div>
    </div>

    <div v-else-if="error" class="row mt-3">
      <div class="col">
        <b-alert variant="danger" :show="true">
          {{ $t('leadDoesNotExist', { leadCode: $route.params.code }) }}
        </b-alert>
      </div>
    </div>

    <spinner v-else :display-loading="true" />

    <b-modal v-if="lead" id="archive-confirmation" centered title="Confirmation" @ok="archivedState">
      <template #modal-header="{ close }">
        <h5 class="modal-title">
          {{ $t('Confirmation') }}
        </h5>
        <b-button class="close" @click.stop="close">
          <font-awesome-icon icon="times" />
        </b-button>
      </template>
      <p class="my-4">
        {{ $t('Are you sure you want to {archiveState} this lead?', { archiveState: archived ? $t('unarchive') : $t('archive') }) }}
      </p>
      <p class="my-4">
        <i18n path="This will {shownState} the lead in the list view of {myLeads}." :shownState="archived ? $t('show') : $t('hide')">
          <template v-slot:myLeads>
            <router-link class="text-link" :to="{name:'my-leads'}">
              {{ $t('myLeads') }}
            </router-link>
          </template>
        </i18n>
        <br>
        {{ $t('You can always {archiveState} it later.', { archiveState: archived ? $t('unarchive') : $t('archive') }) }}
      </p>
      <template #modal-footer="{ ok, cancel }">
        <b-button size="sm" variant="outline-secondary" @click="cancel()">
          {{ $t('Cancel') }}
        </b-button>
        <b-button size="sm" variant="primary" @click="ok()">
          {{ $t('OK') }}
        </b-button>
      </template>
    </b-modal>
    <b-modal v-if="lead" id="feedback-confirmation" centered @ok="requestFeedback">
      <template #modal-header="{ close }">
        <h5 class="modal-title">
          {{ prettyTitle }}
        </h5>
        <b-button class="close" @click.stop="close">
          <font-awesome-icon icon="times" />
        </b-button>
      </template>
      <p class="my-4">
        {{ $t("If you've completed the job and proud of your work, send an email to request a review now.") }}
      </p>
      <template #modal-footer="{ ok, cancel }">
        <b-button size="sm" variant="outline-secondary" @click="cancel()">
          {{ $t('Cancel') }}
        </b-button>
        <b-button size="sm" variant="primary" @click="ok()">
          {{ $t('Send Email') }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import LeadMap from "@/components/map/LeadMap";
import LeadNotes from "@/components/notes/LeadNotes";
import PageHeading from "@/components/control/PageHeading";
import Spinner from "@/components/animation/Spinner";
import Notification from '@/imports/mixins/notification';
import PrintUrl from '@/imports/mixins/printUrl';
import getFeedback from "@/imports/mixins/getFeedback";
import {ACTIVE_LEAD} from "@/store/leads/mutation-types";
import userDevice from "@/imports/mixins/userDevice";
import LeadReview from "@/components/section/LeadReview";
import FinanceModule from "@/components/section/LeadFinanceModule";
import dates from "@/services/date";
import company from "@/imports/mixins/company";
import companyAccount from "@/imports/mixins/companyAccount";
import workTypeTranslation from "@/imports/mixins/workTypeTranslation";

const GOOGLE_MAPS_SEARCH_URL = process.env.VUE_APP_GOOGLE_MAPS_SEARCH_URL;

export default {
  name: "MyLead",
  components: {LeadReview, Spinner, PageHeading, LeadNotes, LeadMap, FinanceModule},
  mixins: [Notification, PrintUrl, getFeedback, userDevice, company, workTypeTranslation, companyAccount],
  title() {
    if (this.lead !== null) {
      return `Lead ${this.lead.id}`;
    }
  },
  data: function() {
    return {
      questions: null,
      invoice: null,
      credit: null,
      remindFeedback: true,
      feedbackTooLate: false,
      dismissableError: null,
      leadMessage: null,
      reportView: false,
      error: null,
      note: null,
      noteBusy: null,
      reportBanned: false,
    }
  },
  computed: {
    lead: {
      get: function() {
        return this.$store.state.leads.ACTIVE_LEAD;
      },
      set: function(value) {
        this.$store.commit(ACTIVE_LEAD, value);
      }
    },
    feedbackReminderTitle: function() {
      if (this.feedbackTooLate) {
        return this.$t('Too much time has passed to request a review from this customer.\n') +
          this.$t('We allow a window of up to 20 weeks to gather feedback.')
      }
      if (!this.remindFeedback) {
        return this.$t('You have already requested a review from this customer.')
      }
      return '';
    },
    invoiceCode: function() {
      return (this.invoice) ? this.invoice.code : null;
    },
    creditCode: function() {
      return (this.credit) ? this.credit.code : null;
    },
    mapMarker: function() {
      return {lat: this.lead.enquiry.latitude, lng: this.lead.enquiry.longitude};
    },
    invoiceUrl: function() {
      return (this.invoice !== null)
        ? process.env.VUE_APP_DOWNLOAD_URL + 'invoice/' + this.invoice.code
        : null
    },
    reportExpired: function() {
      return dates.pastDateTime(dates.parseJsonDate(this.reportBy));
    },
    appealExpired: function() {
      return (this.lead.isAppealable) ? dates.pastDateTime(dates.parseJsonDate(this.appealBy)) : false;
    },
    isExtended:  function() {
      return this.lead.reason === null && this.lead.extensions_granted === true;
    },
    reportBy: function() {
      return dates.parseJsonDate(this.lead.report_by);
    },
    reportByFormatted: function() {
      return dates.jsonDateToLocaleDateTimeNS(this.lead.report_by, 'en-GB', 'GMT');
    },
    appealBy: function() {
      return this.lead.appeal_by;
    },
    appealByParsed: function() {
      return dates.parseJsonDate(this.lead.appeal_by);
    },
    isReportable: function() {
      return (
        (this.lead.reportStatus === null || this.isExtended)
        && this.reportExpired === false
        && !this.reportBanned
        && !this.lead.outstanding_report
        && (this.lead.query_result === null && !this.lead.isAppealable)
      );
    },
    pageTitle: function() {
      return (this.lead) ? this.getDescriptionTranslation(this.lead.enquiry.work_type) : null;
    },
    backPage: function() {
      return this.$route.params.fromPage;
    },
    prettyTitle: function() {
      return 'Ask ' + this.lead.enquiry.title + ' ' + this.lead.enquiry.forename + ' ' + this.lead.enquiry.surname + ' for a Review';
    },
    archived:{
      get: function() {
        return (this.lead && this.lead.dashboard_lead) ? this.lead.dashboard_lead.archived : false;
      },
      set: function(value) {
        this.lead.dashboard_lead.archived = value;
      }
    },
    reportTooltip: function() {
      return this.lead.outstanding_report
        ? this.$t('This lead is already reported')
        : ((this.reportBanned)
          ? this.$t('Reporting is not enabled for this lead type')
          : (!this.isReportable
            ? this.$t('Your ability to report this lead closed on ') + dates.jsonDateToLocaleDateTime(this.reportBy, 'en-GB')
            : ''
          )
        )
    }
  },
  methods: {
    saveNote: function(text) {
      this.noteBusy = true;
      this.$api.leads.addNote(text, this.lead.code).then(response => {
        let newNote = response.data.data.note;
        this.lead.notes = [...this.lead.notes, newNote];
        this.noteBusy = false;
      });
    },
    archivedState: function() {
      if (!this.lead.dashboard_lead) {
        this.lead.dashboard_lead = {archived: false};
      }
      this.lead.dashboard_lead.archived = (this.lead.dashboard_lead) ? !this.lead.dashboard_lead.archived : true;
      const newStatus = (this.lead.dashboard_lead && this.lead.dashboard_lead.archived) ? "archived" : "unarchived";
      this.$store.dispatch('toggleLeadArchived',this.lead.code).then(() => {
        this.notify(
          this.$bvToast,
          `Lead successfully ${newStatus}`,
          null,
          'success'
        );
      });
    },
    askConfirmation() {
      this.$bvModal.show('archive-confirmation');
    },
    openInNewTab: function(enquiryDetails) {
      if (this.isMobile) {
        const url = this.buildUrl(enquiryDetails);
        const newBrowserTab = window.open(url, '_blank');
        newBrowserTab.focus();
      }
    },
    buildUrl: function(enquiryDetails) {
      const {address1, address2, town, postcode} = enquiryDetails;
      let addressLine1 = (address1 !== null && address1 !== undefined) ? address1 : '';
      let addressLine2 = (address2 !== null && address2 !== undefined) ? address2 : '';
      return`${GOOGLE_MAPS_SEARCH_URL}${addressLine1} ${addressLine2} ${town}, ${postcode}`;
    },
    generateLeadDetailsPdf: function (lead) {
      return this.printUrl(lead.code + (this.isMobile ? '' : '?print=0'));
    },
    getLead: function() {
      this.$api.leads.getLead(this.$route.params.code).then(response => {
        this.invoice = response.data.data.invoice;
        this.credit = response.data.data.credit;
        this.remindFeedback = response.data.data.remindFeedback;
        this.feedbackTooLate = response.data.data.feedbackTooLate;
        this.leadMessage = response.data.data.leadMessage;
        this.lead = response.data.data.lead;
        this.reportBanned = response.data.data.isReportBanned;
        let leadQuestions = response.data.data.questions;
        let result = leadQuestions.filter(question => question.question !== 'Description of proposed work' && question.question !== 'Qualified');
        this.questions = [
          {'question': this.$t('Date'), 'answer': this.$options.filters.parseDateTimeNs(this.lead.enquiry.hold_release_date)},
          {'question': this.$t('Lead Type'), 'answer': this.getDescriptionTranslation(this.lead.enquiry.work_type)},
          ...result,
          {'question': this.$t('Lead ID'), 'answer': this.lead.code},
        ];
      }).catch(() => {
        this.lead = null;
        this.error = true;
      });
    }
  },
  watch: {
    note: function(value) {
      if (value !== null) {
        this.saveNote(value);
      }
    }
  },
  created: function () {
    this.lead = null;
    this.error = null;
    this.getLead();
  },
}
</script>

<style scoped lang="scss">
.administration__actions {
  @media only screen and (min-width: 1670px) {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
  @media only screen and (min-width: 1400px) and (max-width: 1670px) {
    padding-left: .5rem;
    padding-right: .5rem;
  }

  .btn {
    float: left;
    border-radius: 0.188rem;
    font-size: $font-size-14;

    @media only screen and (max-width: 992px) {
      flex: 0 1 80%;
      margin: 0 auto;
    }

    @media only screen and (min-width: 992px) and (max-width: 1670px) {
      flex: 0 1 47%;
    }

    @media only screen and (min-width: 1670px) {
      flex: 0 1 45%;
    }

    @media only screen and (max-width: 1670px) {
      padding: .563rem .45rem;
    }
  }
}

@include media-breakpoint-only(sm) {
  .lead__sidebar {
    justify-content: space-between;
    display: flex;
    flex-wrap: wrap;
    .card {
      flex: 0 1 49%;
      max-width: 50%;
      margin-bottom: 20px;

      &.full-width {
        flex: 0 1 100%;
        max-width: 100%;
      }
    }
  }
}

.dd__text,
.dt__icon {
  float: left;
  margin-bottom: 10px;
}

.dt__icon {
  width: 14px;
  @include media-breakpoint-up(sm) {
    margin-right: 25px;
  }
  @include media-breakpoint-down(sm) {
    margin-right: 15px;
  }
  text-align: center;
}

.dd__text {
  width: calc(100% - 40px);
}

.lead__details {
  font-size: $font-size-14;
}

.customer-notes {
  background-color: $body-bg;
}

::v-deep {
  .table {
    tbody {
      tr {
        &:first-child {
          width: 60%;
        }

        td:last-child {
          font-weight: $headings-font-weight;
        }
      }
    }
  }

  .page-header {
    .col {
      max-width: 120px;
    }
  }
  .page-header__title {
    max-width: calc(100% - 120px);
    text-align: left;
  }
}
</style>
